// ============================================================
// applyHits/ledger - the working state of one hit application.
//
// Every stage (crits, death, effects, the entry) reads the same target
// and appends to the same lists: the conditions and armour the target
// ends with, the info lines logged after the attack entry, and the events
// the structured entry contains. The ledger is that shared state, opened
// once per `applyHits` call and written to the tab once, at the end.
// ============================================================
import type { StackPolicy } from '../../engine/stackPolicy';
import { activeProtagonist, type Protagonist } from '../../engine/survival/protagonist';
import { getBodyConfig } from '../../engine/index';
import type { WeaponEffectDetail } from '../../engine/weaponEffects';
import type { NarrativeEntry, Tab, Target } from '../../types';
import type { HitResult } from '../../engine/damageCalc';
import type { ApplyHitsData } from './types';

export interface HitLedger {
  tabId: string;
  tab: Tab;
  tgt: Target;
  data: ApplyHitsData;
  sp: string[];
  tName: string;
  stacking: StackPolicy;
  protagonistRules: boolean;
  /** The target's body type - distinct locations let Crippled/Destroyed name the hit location. */
  bodyConfig: ReturnType<typeof getBodyConfig>;
  /** The target was dead before this attack. */
  alreadyDead: boolean;
  /** A critical killed them - until a death floor says otherwise. */
  targetKilled: boolean;
  totalDamage: number;
  /** The conditions the target ends with - one array, mutated in place by every stage. */
  newConds: string[];
  newArmour: Target['armour'];
  /** Info lines logged after the attack entry. */
  extraNarrative: NarrativeEntry[];
  /** Events the structured entry contains. */
  containedEvents: WeaponEffectDetail[];
}

/** Log an info line under the attack. */
export function narr(ledger: HitLedger, text: string): void {
  ledger.extraNarrative.push({ type: 'info' as const, text, targetName: ledger.tName, timestamp: Date.now() });
}

/** Log an info line AND record it as an event of the structured entry. */
export function narrEvent(ledger: HitLedger, event: WeaponEffectDetail & { result: string }): void {
  narr(ledger, event.result);
  ledger.containedEvents.push(event);
}

/** Replace an array's contents in place - the stages share `newConds` by reference. */
export function replaceAll<T>(target: T[], items: readonly T[]): void {
  target.length = 0;
  items.forEach((item) => target.push(item));
}

/**
 * THE CHIP LIMIT. The damage floor multiplies with rate of fire - floor 2
 * against ten attacks is twenty wounds a round. So the floor helps only the
 * first N hits; the rest are refunded the help and resolve as they always
 * would, which against heavy armour means zero.
 *
 * Budgeted HERE rather than in `resolveHitsAgainst` because defence removes
 * whole hits AFTER damage is computed: spending the budget at damage time
 * would spend it on hits the defender then dodged. These are the hits he
 * actually ate, in order. Mutates the hits in place.
 */
function refundChipLimit(prot: Protagonist | undefined, hits: HitResult[]): void {
  if (prot?.chipLimit === undefined) return;
  const limit = prot.chipLimit;
  hits.forEach((h, i) => {
    if (i < limit) return;
    const refund = h.damageFloorApplied || 0;
    if (refund > 0) h.effectiveDamage -= refund;
  });
}

/** How the hook was configured, for the ledger. */
export interface LedgerRules {
  stacking: StackPolicy;
  protagonistRules: boolean;
}

/** Open the ledger: the chip limit applied, then the target as the attack found it. */
export function openLedger(tabId: string, tab: Tab, data: ApplyHitsData, rules: LedgerRules): HitLedger {
  const tgt = tab.target;
  refundChipLimit(activeProtagonist(tgt, rules.protagonistRules), data.hitsAfterDefence);
  const totalDamage = data.hitsAfterDefence.reduce((s, h) => s + h.effectiveDamage, 0);
  const bodyConfig = getBodyConfig(tgt.bodyType || 'humanoid');
  const newConds = [...(data.cdResult?.newConds || tgt.conditions || [])];
  return {
    tabId,
    tab,
    tgt,
    data,
    sp: data.slot.specialRules || [],
    tName: tgt.name || tab.label || '',
    stacking: rules.stacking,
    protagonistRules: rules.protagonistRules,
    bodyConfig,
    alreadyDead: newConds.includes('K'),
    targetKilled: false,
    totalDamage,
    newConds,
    newArmour: { ...tgt.armour },
    extraNarrative: [],
    containedEvents: [],
  };
}
